class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    if (this.mainDetailsToggle) {
      const summary = this.mainDetailsToggle.querySelector('summary');
      this.content = summary ? summary.nextElementSibling : null;

      this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
      this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    this.onMouseEnter = this.onMouseEnter.bind(this);
    this.onMouseLeave = this.onMouseLeave.bind(this);
    this.onFocusIn = this.onFocusIn.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.hoverMedia = window.matchMedia('(hover: hover) and (min-width: 990px)');
    this.closeTimeout = null;
    this.hoverTarget = null;
  }

  connectedCallback() {
    // Re-query on connect: constructor may run before children are parsed,
    // and theme-editor section re-renders create fresh nodes.
    this.mainDetailsToggle = this.mainDetailsToggle || this.querySelector('details');
    if (!this.mainDetailsToggle) return;
    if (!this.content) {
      const summary = this.mainDetailsToggle.querySelector('summary');
      this.content = summary ? summary.nextElementSibling : null;
    }
    if (!this.header) this.header = document.querySelector('.header-wrapper');

    // Hover on the wrapping <li> (larger, includes gap to panel) instead of
    // <header-menu> alone, whose box is tight around the summary and caused
    // instant mouseleave flicker.
    this.hoverTarget = this.closest('li') || this;

    if (this.hoverMedia.matches) this.addHoverListeners();
    const onMediaChange = (event) => {
      if (event.matches) {
        this.addHoverListeners();
      } else {
        this.removeHoverListeners();
        this.close();
      }
    };
    if (typeof this.hoverMedia.addEventListener === 'function') {
      this.hoverMedia.addEventListener('change', onMediaChange);
    } else if (typeof this.hoverMedia.addListener === 'function') {
      this.hoverMedia.addListener(onMediaChange);
    }
    this.addNestedSubmenuHover();
  }

  disconnectedCallback() {
    this.removeHoverListeners();
    if (this.closeTimeout) clearTimeout(this.closeTimeout);
  }

  addHoverListeners() {
    if (!this.hoverTarget || this.hoverTarget.dataset.hoverBound) return;
    this.hoverTarget.dataset.hoverBound = 'true';
    this.hoverTarget.addEventListener('mouseenter', this.onMouseEnter);
    this.hoverTarget.addEventListener('mouseleave', this.onMouseLeave);
    this.addEventListener('focusin', this.onFocusIn);
    this.addEventListener('keydown', this.onKeyDown);
  }

  removeHoverListeners() {
    if (this.hoverTarget && this.hoverTarget.dataset.hoverBound) {
      delete this.hoverTarget.dataset.hoverBound;
      this.hoverTarget.removeEventListener('mouseenter', this.onMouseEnter);
      this.hoverTarget.removeEventListener('mouseleave', this.onMouseLeave);
    }
    this.removeEventListener('focusin', this.onFocusIn);
    this.removeEventListener('keydown', this.onKeyDown);
  }

  hoverEnabled() {
    return this.hoverMedia && this.hoverMedia.matches;
  }

  closeOthers() {
    document.querySelectorAll('header-menu details[open]').forEach((details) => {
      if (details !== this.mainDetailsToggle) {
        details.removeAttribute('open');
        const s = details.querySelector('summary');
        if (s) s.setAttribute('aria-expanded', 'false');
      }
    });
  }

  open() {
    if (!this.mainDetailsToggle) return;
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }
    this.closeOthers();
    this.mainDetailsToggle.setAttribute('open', '');
    const s = this.mainDetailsToggle.querySelector('summary');
    if (s) s.setAttribute('aria-expanded', 'true');
    this.onToggle();
  }

  scheduleClose() {
    if (this.closeTimeout) clearTimeout(this.closeTimeout);
    // Short grace period so moving across the 1-2px gap to the panel doesn't flicker shut.
    this.closeTimeout = setTimeout(() => this.close(), 120);
  }

  onMouseEnter() {
    if (!this.hoverEnabled()) return;
    this.open();
  }

  onMouseLeave() {
    if (!this.hoverEnabled()) return;
    this.scheduleClose();
  }

  onFocusIn(event) {
    if (!this.hoverEnabled()) return;
    if (this.contains(event.target)) this.open();
  }

  onKeyDown(event) {
    if (event.key === 'Escape') this.close();
  }

  addNestedSubmenuHover() {
    // Second-level dropdown (plain <details> inside .header__submenu): also open on hover on desktop.
    this.querySelectorAll('.header__submenu details').forEach((nestedDetails) => {
      const summary = nestedDetails.querySelector(':scope > summary');
      const parentItem = nestedDetails.parentElement;
      if (!summary || !parentItem || parentItem.dataset.hoverBound) return;
      parentItem.dataset.hoverBound = 'true';
      let nestedTimer = null;
      parentItem.addEventListener('mouseenter', () => {
        if (!this.hoverEnabled()) return;
        if (nestedTimer) clearTimeout(nestedTimer);
        nestedDetails.setAttribute('open', '');
        summary.setAttribute('aria-expanded', 'true');
      });
      parentItem.addEventListener('mouseleave', () => {
        if (!this.hoverEnabled()) return;
        if (nestedTimer) clearTimeout(nestedTimer);
        nestedTimer = setTimeout(() => {
          nestedDetails.removeAttribute('open');
          summary.setAttribute('aria-expanded', 'false');
        }, 120);
      });
    });
  }

  onToggle() {
    if (!this.header || !this.mainDetailsToggle) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

customElements.define('header-menu', HeaderMenu);

// Fallback: if custom-element upgrade failed for any reason, still open menus on hover via delegation.
(() => {
  const media = window.matchMedia('(hover: hover) and (min-width: 990px)');
  const isTopLevel = (details) => details.parentElement instanceof HTMLElement && details.parentElement.tagName.toLowerCase() === 'header-menu';
  document.addEventListener(
    'mouseover',
    (event) => {
      if (!media.matches) return;
      const li = event.target.closest('.header__inline-menu li');
      if (!li) return;
      const headerMenu = li.querySelector(':scope > header-menu > details, :scope > details');
      if (!headerMenu || headerMenu.hasAttribute('open')) return;
      // Only auto-open top-level + nested submenu details, never the mobile drawer.
      if (!li.closest('.header__inline-menu')) return;
      if (isTopLevel(headerMenu) || headerMenu.closest('.header__submenu')) {
        document.querySelectorAll('header-menu details[open]').forEach((d) => {
          if (d !== headerMenu) d.removeAttribute('open');
        });
        headerMenu.setAttribute('open', '');
      }
    },
    { passive: true }
  );
  document.addEventListener(
    'mouseout',
    (event) => {
      if (!media.matches) return;
      const li = event.target.closest('.header__inline-menu li');
      if (!li) return;
      if (li.contains(event.relatedTarget)) return;
      const openDetails = li.querySelector(':scope > header-menu > details[open], :scope > details[open]');
      if (openDetails && (isTopLevel(openDetails) || openDetails.closest('.header__submenu'))) {
        const summary = openDetails.querySelector(':scope > summary');
        // Don't force-close while keyboard focus is inside.
        if (openDetails.contains(document.activeElement)) return;
        openDetails.removeAttribute('open');
        if (summary) summary.setAttribute('aria-expanded', 'false');
      }
    },
    { passive: true }
  );
})();
